import { isSchemaNode, SchemaNode } from './types';

const mergeNode = (
  originalNode: SchemaNode,
  localNode: SchemaNode | undefined,
  remoteNode: SchemaNode,
  localModifiedIds: Set<string>
): SchemaNode => {
  const nodeId = originalNode.id;
  const useLocal = nodeId && localModifiedIds.has(nodeId);

  const baseNode = useLocal && localNode ? localNode : remoteNode;

  if (!baseNode.children) {
    return { ...baseNode };
  }

  if (Array.isArray(baseNode.children)) {
    const originalChildren = Array.isArray(originalNode.children)
      ? originalNode.children
      : [];
    const localChildren =
      localNode && Array.isArray(localNode.children) ? localNode.children : [];
    const remoteChildren = baseNode.children;

    return {
      ...baseNode,
      children: mergeNodeArrays(
        originalChildren,
        localChildren,
        remoteChildren,
        localModifiedIds
      ),
    };
  }

  if (isSchemaNode(baseNode.children)) {
    const originalChild = isSchemaNode(originalNode.children)
      ? originalNode.children
      : undefined;
    const localChild =
      localNode && isSchemaNode(localNode.children)
        ? localNode.children
        : undefined;

    if (originalChild) {
      return {
        ...baseNode,
        children: mergeNode(
          originalChild,
          localChild,
          baseNode.children,
          localModifiedIds
        ),
      };
    }
  }

  return { ...baseNode };
};

const mergeNodeArrays = (
  original: SchemaNode[],
  local: SchemaNode[],
  remote: SchemaNode[],
  localModifiedIds: Set<string>
): SchemaNode[] => {
  const originalById = new Map<string, SchemaNode>();
  const localById = new Map<string, SchemaNode>();

  original.forEach(node => {
    if (node.id) originalById.set(node.id, node);
  });
  local.forEach(node => {
    if (node.id) localById.set(node.id, node);
  });

  const result: SchemaNode[] = remote.map(remoteNode => {
    if (!remoteNode.id) return { ...remoteNode };

    const originalNode = originalById.get(remoteNode.id);
    const localNode = localById.get(remoteNode.id);

    if (!originalNode) {
      return { ...remoteNode };
    }

    return mergeNode(originalNode, localNode, remoteNode, localModifiedIds);
  });

  local.forEach(localNode => {
    if (!localNode.id) return;

    const isLocallyAdded =
      !originalById.has(localNode.id) && localModifiedIds.has(localNode.id);
    const existsInRemote = remote.some(r => r.id === localNode.id);

    if (isLocallyAdded && !existsInRemote) {
      result.push({ ...localNode });
    }
  });

  return result;
};

export const mergeSchemaContents = (
  original: unknown,
  local: unknown,
  remote: unknown,
  localModifiedIds: Set<string>
): unknown => {
  if (
    !Array.isArray(original) ||
    !Array.isArray(local) ||
    !Array.isArray(remote)
  ) {
    return remote;
  }

  return mergeNodeArrays(original, local, remote, localModifiedIds);
};
